import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Search, MapPin, Users } from 'lucide-react';
import { exploreSystems, addRecommendedSystem, searchTrailAreas, getAreaTrails, importAreaTrails } from '../api';
import { useToast } from '../components/Toast';

export default function Explore() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [trails, setTrails] = useState([]);
  const [loadingTrails, setLoadingTrails] = useState(false);
  const [importing, setImporting] = useState(false);
  const toast = useToast();
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    exploreSystems()
      .then(res => setSystems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleSearch = useCallback((value) => {
    setQuery(value);
    setSelectedArea(null);
    setTrails([]);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || value.trim().length < 3) {
      setAreas([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchTrailAreas(value);
        setAreas(res.data || []);
      } catch {
        setAreas([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  async function handleSelectArea(area) {
    setSelectedArea(area);
    setAreas([]);
    setLoadingTrails(true);
    try {
      const [south, north, west, east] = area.boundingbox;
      const res = await getAreaTrails({ south, west, north, east });
      setTrails(res.data || []);
    } catch {
      toast.error('Failed to fetch trails for this area.');
    } finally {
      setLoadingTrails(false);
    }
  }

  async function handleImport() {
    if (!selectedArea) return;
    setImporting(true);
    try {
      const [south, north, west, east] = selectedArea.boundingbox;
      await importAreaTrails({
        name: selectedArea.name,
        south, west, north, east,
        lat: selectedArea.lat,
        lng: selectedArea.lng,
      });
      toast.success(`Imported ${selectedArea.name}!`);
      setSelectedArea(null);
      setTrails([]);
      setQuery('');
      load();
    } catch {
      toast.error('Import failed.');
    } finally {
      setImporting(false);
    }
  }

  async function handleAdd(system) {
    setAddingId(system._id);
    try {
      await addRecommendedSystem(system._id);
      toast.success(`Added ${system.name} to your systems!`);
      setSystems(prev => prev.filter(s => s._id !== system._id));
    } catch {
      toast.error('Failed to add system.');
    } finally {
      setAddingId(null);
    }
  }

  // Filter DB systems by search query
  const filtered = query.trim().length >= 2
    ? systems.filter(s =>
        s.name.toLowerCase().includes(query.toLowerCase()) ||
        (s.region || '').toLowerCase().includes(query.toLowerCase())
      )
    : systems;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-header__title">EXPLORE</h1>
        <p className="page-header__subtitle">Discover new trail systems to add to your list.</p>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '28px' }}>
        <div className="search-input">
          <div className="search-input__icon"><Search size={16} /></div>
          <input
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by name or search new areas (e.g. Whistler, Moab)..."
          />
          {searching && (
            <div className="search-input__spinner">
              <span className="spinner" style={{ width: 16, height: 16 }} />
            </div>
          )}
        </div>

        {/* OSM search results dropdown */}
        {areas.length > 0 && !selectedArea && (
          <div className="dropdown">
            <div className="dropdown__label">
              Search new areas on OpenStreetMap
            </div>
            {areas.map(a => (
              <button
                key={`${a.osmType}-${a.id}`}
                onClick={() => handleSelectArea(a)}
                className="dropdown__item"
              >
                <div className="dropdown__item-row">
                  <MapPin size={13} className="text-muted" style={{ flexShrink: 0 }} />
                  <span className="dropdown__item-name">{a.name}</span>
                  {a.userCount > 0 && (
                    <span className="pill pill--riders">
                      {a.userCount} rider{a.userCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="dropdown__item-sub" style={{ paddingLeft: '21px' }}>{a.fullName}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Import preview for selected OSM area */}
      {selectedArea && (
        <div className="card" style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <div className="system-card__name">{selectedArea.name}</div>
              <div className="explore-card__meta">{selectedArea.fullName}</div>
            </div>
            <button onClick={() => { setSelectedArea(null); setTrails([]); }} className="btn btn--secondary btn--sm">Cancel</button>
          </div>

          {loadingTrails ? (
            <div className="loading loading--sm"><span className="spinner" /></div>
          ) : trails.length > 0 ? (
            <>
              <div style={{ fontSize: '13px', marginBottom: '12px' }} className="text-muted">
                {trails.length} trail{trails.length !== 1 ? 's' : ''} found
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'grid', gap: '6px', marginBottom: '16px' }}>
                {trails.map((t, i) => (
                  <div key={i} className="trail-preview">
                    <span className="trail-preview__name">{t.name}</span>
                    <div className="trail-preview__meta">
                      {t.length && <span className="trail-preview__length">{t.length} km</span>}
                      <span className={`badge badge--${t.difficulty}`}>{t.difficulty}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn--primary btn--full" onClick={handleImport} disabled={importing}>
                {importing ? <span className="spinner" /> : `IMPORT ${trails.length} TRAILS`}
              </button>
            </>
          ) : (
            <p className="text-muted text-center" style={{ padding: '20px' }}>
              No tagged MTB trails found in this area.
            </p>
          )}
        </div>
      )}

      {/* Browse existing systems */}
      {loading ? (
        <div className="loading loading--md"><div className="spinner spinner--lg" /></div>
      ) : filtered.length > 0 ? (
        <>
          <div className="section-header" style={{ marginBottom: '12px' }}>
            <h2 style={{ fontSize: '20px' }}>
              {query.trim().length >= 2 ? 'MATCHING SYSTEMS' : 'ALL SYSTEMS'}
            </h2>
            <span className="section-header__meta">{filtered.length} available</span>
          </div>
          <div className="grid grid--gap-md">
            {filtered.map(system => (
              <div key={system._id} className="card explore-card">
                <div className="flex-1">
                  <div className="explore-card__name">{system.name}</div>
                  <div className="explore-card__meta">
                    {system.region && <span>{[system.region, system.country].filter(Boolean).join(', ')}</span>}
                    <span>{system.trailCount} trail{system.trailCount !== 1 ? 's' : ''}</span>
                    {system.userCount > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <Users size={11} /> {system.userCount} rider{system.userCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => handleAdd(system)}
                  disabled={addingId === system._id}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  {addingId === system._id ? <span className="spinner spinner--sm" /> : <><Plus size={14} /> ADD</>}
                </button>
              </div>
            ))}
          </div>
        </>
      ) : systems.length === 0 ? (
        <div className="empty-state">
          <p>No trail systems in the database yet. Use the search above to discover and import trail areas.</p>
        </div>
      ) : query.trim().length >= 2 ? (
        <div className="empty-state">
          <p>No matching systems found. Try the search above to discover new areas on OpenStreetMap.</p>
        </div>
      ) : null}
    </div>
  );
}
